/**
 * useGettingStarted — computes the "Getting Started" onboarding checklist state
 * from existing endpoints (no aggregate endpoint), plus dismissal/celebration
 * persistence.
 *
 * All five item states derive from data the app already fetches:
 * - create-tank    → useTank().tanks
 * - first-data     → device-mapped parameters (useTankParameters) OR any latest
 *                    metric value (useLatestMetrics). The latter flips as soon as
 *                    a manual water test is logged, since manual entries land in
 *                    the measurements table that the latest-metrics endpoint reads
 *                    regardless of device mapping.
 * - target-ranges  → useTankTargetRanges() (same hook + cache the manual entry
 *                    modal already reads; key ['tank-target-ranges', tankId])
 * - first-task     → useTaskDefinitions() (same source + query key as TasksPage)
 * - notifications  → useNotificationPreferences()
 *
 * Dismissal & celebration are persisted SERVER-SIDE via the user's
 * `dismissed_hints` (through `useOnboarding`). Each checklist flag maps to a
 * `dismissed_hints` entry (see `HINT_KEYS` below). localStorage is retained as a
 * READ-ONLY fallback so users who dismissed before this migration keep their
 * dismissal; all writes now go to the server. The swap is isolated to the
 * `gettingStartedStorage` object (reads) and the `dismissHint` write calls.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTank } from '@/contexts/TankContext';
import { useAuth } from '@/contexts/AuthContext';
import { useTankParameters } from '@/hooks/useTankParameters';
import { useLatestMetrics } from '@/hooks/useLatestMetrics';
import { useTaskDefinitions } from '@/hooks/useTasks';
import { useNotificationPreferences } from '@/hooks/useNotificationPreferences';
import { useOnboarding } from '@/hooks/useOnboarding';
import { useTankTargetRanges } from '@/api/measurements';
import { hasAnyMetricData } from '@/utils/hasAnyMetricData';
import { trackOnboarding } from '@/lib/onboardingAnalytics';

export type GettingStartedItemKey =
  | 'create-tank'
  | 'first-data'
  | 'target-ranges'
  | 'first-task'
  | 'notifications';

export interface GettingStartedItem {
  key: GettingStartedItemKey;
  label: string;
  /** Short description shown under the label. */
  description: string;
  done: boolean;
  /** Deep-link route for the item's CTA. Absent when the item opens a modal. */
  to?: string;
  /** Non-navigation action; `open-entry` opens the UnifiedManualEntryModal. */
  action?: 'open-entry';
  /**
   * Side effect to run when the row is activated (in addition to any `to`
   * navigation). Used by the target-ranges review item to persist that the
   * user has clicked through to review their ranges.
   */
  onSelect?: () => void;
}

export interface UseGettingStartedResult {
  items: GettingStartedItem[];
  completedCount: number;
  allDone: boolean;
  /**
   * True only once every constituent query has settled (success or error) and
   * the tank context has initialized. The card renders nothing until this is
   * true so completion state never flashes in from a loading placeholder.
   */
  isReady: boolean;
  /**
   * True when the one-time graduation celebration should be shown. Only ever
   * true for a user who saw the checklist incomplete and then completed it — a
   * veteran whose first settled evaluation is already allDone is celebrated
   * silently and never sees it.
   */
  showCelebration: boolean;
  dismissed: boolean;
  dismiss: () => void;
  restore: () => void;
  celebrated: boolean;
  markCelebrated: () => void;
}

/** Legacy localStorage keys — retained for READ-ONLY pre-migration fallback. */
const DISMISSED_KEY = 'gettingStartedDismissed';
const CELEBRATED_KEY = 'gettingStartedCelebrated';
const SEEN_INCOMPLETE_KEY = 'gettingStartedSeenIncomplete';
const TARGET_RANGES_REVIEWED_KEY = 'gettingStartedTargetRangesReviewed';

/**
 * Server-side `dismissed_hints` keys each checklist flag maps to. These are the
 * durable source of truth; the legacy localStorage keys above are only a
 * read-fallback for dismissals made before this migration.
 */
export const HINT_KEYS = {
  dismissed: 'getting-started-dismissed',
  celebrated: 'getting-started-celebrated',
  seenIncomplete: 'getting-started-seen-incomplete',
  targetRangesReviewed: 'target-ranges-reviewed',
  /**
   * Durable once-ever guard for the `first_data_point` funnel event (design
   * §3.7 — time-to-first-data-point is THE activation metric). A server hint,
   * not localStorage: the metric is per-user, so it must survive a device
   * switch or cache clear and never re-fire. (No legacy localStorage fallback —
   * this hint post-dates the migration.)
   */
  firstDataPointTracked: 'ttfdp-tracked',
} as const;

/** Namespace a storage key by user id; unscoped when no user id is available. */
function scopedKey(base: string, userId?: string): string {
  return userId ? `${base}:${userId}` : base;
}

/** Read a legacy localStorage flag (fallback only). */
function readFlag(base: string, userId?: string): boolean {
  try {
    return localStorage.getItem(scopedKey(base, userId)) === 'true';
  } catch {
    return false;
  }
}

/**
 * Write a legacy localStorage flag. Used as an offline-durability dual-write
 * alongside each server hint write: if the (fire-and-forget) PATCH fails, this
 * local fallback keeps the dismissal after a reload since the read path ORs it.
 */
function writeFlag(base: string, userId?: string): void {
  try {
    localStorage.setItem(scopedKey(base, userId), 'true');
  } catch {
    // localStorage may be unavailable (private mode / quota); ignore.
  }
}

/**
 * Legacy localStorage key each server hint dual-writes to for offline
 * durability. Keys mirror the read fallback in `gettingStartedStorage`.
 */
const HINT_LEGACY_KEYS: Record<string, string> = {
  [HINT_KEYS.dismissed]: DISMISSED_KEY,
  [HINT_KEYS.celebrated]: CELEBRATED_KEY,
  [HINT_KEYS.seenIncomplete]: SEEN_INCOMPLETE_KEY,
  [HINT_KEYS.targetRangesReviewed]: TARGET_RANGES_REVIEWED_KEY,
};

/**
 * Single access point for checklist persistence READS. A flag is set when its
 * server hint is present in `dismissed_hints`, OR (pre-migration fallback) when
 * the legacy localStorage flag is set. Writes are performed via
 * `useOnboarding().dismissHint(HINT_KEYS.*)` in the hook below — the server
 * append is idempotent, so there is no corresponding setter here.
 */
export const gettingStartedStorage = {
  isDismissed(dismissedHints: string[], userId?: string): boolean {
    return dismissedHints.includes(HINT_KEYS.dismissed) || readFlag(DISMISSED_KEY, userId);
  },
  isCelebrated(dismissedHints: string[], userId?: string): boolean {
    return dismissedHints.includes(HINT_KEYS.celebrated) || readFlag(CELEBRATED_KEY, userId);
  },
  isSeenIncomplete(dismissedHints: string[], userId?: string): boolean {
    return (
      dismissedHints.includes(HINT_KEYS.seenIncomplete) ||
      readFlag(SEEN_INCOMPLETE_KEY, userId)
    );
  },
  isTargetRangesReviewed(dismissedHints: string[], userId?: string): boolean {
    return (
      dismissedHints.includes(HINT_KEYS.targetRangesReviewed) ||
      readFlag(TARGET_RANGES_REVIEWED_KEY, userId)
    );
  },
  isFirstDataPointTracked(dismissedHints: string[]): boolean {
    // Server hint only — no legacy localStorage fallback (post-migration key).
    return dismissedHints.includes(HINT_KEYS.firstDataPointTracked);
  },
};

/** A react-query result is "settled" once it has data, errored, or is disabled. */
interface QueryLike {
  isSuccess?: boolean;
  isError?: boolean;
  isLoading?: boolean;
  fetchStatus?: string;
}
function querySettled(q: QueryLike): boolean {
  if (q.isSuccess || q.isError) return true;
  // A disabled query (e.g. no tank selected) never fetches; treat it as settled
  // so the card can still render for a tankless user.
  if (q.fetchStatus === 'idle') return true;
  return q.isLoading === false;
}

export function useGettingStarted(): UseGettingStartedResult {
  const { tanks, selectedTank, isInitialized } = useTank();
  const { user } = useAuth();
  const userId = user?.id;
  const { dismissedHints, dismissHint } = useOnboarding();

  const tankParamsQuery = useTankParameters();
  const latestMetricsQuery = useLatestMetrics();
  const taskDefsQuery = useTaskDefinitions();
  const notifPrefsQuery = useNotificationPreferences();
  const targetRangesQuery = useTankTargetRanges(selectedTank?.id);

  // Local state is the instant UI truth (optimistic); server persistence is
  // fired-and-forgotten via dismissHint. Initialized from the server hints (or
  // legacy localStorage fallback) captured at mount.
  const [dismissed, setDismissed] = useState<boolean>(() =>
    gettingStartedStorage.isDismissed(dismissedHints, userId)
  );
  const [celebrated, setCelebrated] = useState<boolean>(() =>
    gettingStartedStorage.isCelebrated(dismissedHints, userId)
  );
  const [targetRangesReviewed, setTargetRangesReviewed] = useState<boolean>(() =>
    gettingStartedStorage.isTargetRangesReviewed(dismissedHints, userId)
  );
  const [showCelebration, setShowCelebration] = useState<boolean>(false);

  // Session-local latch for the first_data_point activation event. Guards against
  // a double-fire before the (fire-and-forget) durable hint round-trips; the
  // 'ttfdp-tracked' hint is the cross-session guard.
  const firstDataTrackedRef = useRef(false);

  // Persist a checklist hint durably. Fire-and-forget by design — the optimistic
  // local state has already flipped, so we never await the PATCH. Two safeguards:
  //  1. Dual-write the user-namespaced legacy localStorage key so the dismissal
  //     survives a reload even when the PATCH fails offline (the read path ORs
  //     this fallback). Gated on a known user id since the fallback is per-user.
  //  2. Swallow the promise rejection so a failed/offline PATCH never surfaces as
  //     an unhandled rejection (useOnboarding's mutation also logs it via onError).
  const persistHint = useCallback(
    (hintKey: string) => {
      if (userId) {
        const legacyKey = HINT_LEGACY_KEYS[hintKey];
        if (legacyKey) writeFlag(legacyKey, userId);
      }
      void dismissHint(hintKey).catch(() => {});
    },
    [dismissHint, userId]
  );

  const dismiss = useCallback(() => {
    setDismissed(true); // optimistic: collapse instantly
    persistHint(HINT_KEYS.dismissed);
  }, [persistHint]);

  // Restore re-expands the card locally. dismissed_hints is append-only
  // server-side, so this is a session-local override (matches prior behavior:
  // the card stays visible until dismissed again).
  const restore = useCallback(() => {
    setDismissed(false);
  }, []);

  const markCelebrated = useCallback(() => {
    setCelebrated(true);
    persistHint(HINT_KEYS.celebrated);
  }, [persistHint]);

  const markTargetRangesReviewed = useCallback(() => {
    setTargetRangesReviewed(true);
    persistHint(HINT_KEYS.targetRangesReviewed);
  }, [persistHint]);

  // first-data flips when ANY data exists for the tank: a device mapping
  // (tank_parameters) OR any latest metric value (measurements table, which is
  // what a manual water-test entry writes). Loading counts as not-done.
  const hasDeviceParams = (tankParamsQuery.data?.parameters?.length ?? 0) > 0;
  const hasLatestMetric = hasAnyMetricData(latestMetricsQuery.data);
  const hasData = hasDeviceParams || hasLatestMetric;

  const hasTask = (taskDefsQuery.data?.definitions?.length ?? 0) > 0;
  const notifPrefs = notifPrefsQuery.data;
  const notificationsOn = !!(notifPrefs?.enabled || notifPrefs?.setup_completed);

  const items = useMemo<GettingStartedItem[]>(
    () => [
      {
        key: 'create-tank',
        label: 'Create a tank',
        description: 'Everything in Reef DataHub belongs to a tank.',
        done: tanks.length > 0,
        to: '/tanks',
      },
      {
        key: 'first-data',
        label: 'Add your first data',
        description: 'Connect a device or log a water test.',
        done: hasData,
        action: 'open-entry',
      },
      {
        key: 'target-ranges',
        label: 'Review your target ranges',
        description: 'Tiles and range bars need targets to be meaningful.',
        // Tank creation auto-seeds default ranges, so their mere existence can't
        // signal intent. This is a click-through review item: done once the user
        // has opened Settings to review, persisted via gettingStartedStorage.
        done: targetRangesReviewed,
        to: '/settings?tab=targets',
        onSelect: markTargetRangesReviewed,
      },
      {
        key: 'first-task',
        label: 'Create a maintenance task',
        description: 'Track water changes, dosing, and testing.',
        done: hasTask,
        to: '/tasks',
      },
      {
        key: 'notifications',
        label: 'Turn on task reminders',
        description: 'Get a nudge when tasks are due.',
        done: notificationsOn,
        to: '/settings?tab=notifications',
      },
    ],
    [tanks.length, hasData, targetRangesReviewed, markTargetRangesReviewed, hasTask, notificationsOn]
  );

  const completedCount = items.filter((item) => item.done).length;
  const allDone = completedCount === items.length;

  const isReady =
    isInitialized &&
    querySettled(tankParamsQuery) &&
    querySettled(latestMetricsQuery) &&
    querySettled(taskDefsQuery) &&
    querySettled(notifPrefsQuery) &&
    querySettled(targetRangesQuery);

  // Celebration decision. Runs only once the checklist is ready so a loading
  // placeholder never counts as "incomplete" or "complete".
  //  - Not-done yet → remember that the user has seen it incomplete.
  //  - Done, and previously seen incomplete → genuine transition: celebrate once.
  //  - Done at the very first ready evaluation (veteran) → mark celebrated
  //    silently; never show the celebration.
  useEffect(() => {
    if (!isReady) return;
    if (!allDone) {
      if (!gettingStartedStorage.isSeenIncomplete(dismissedHints, userId)) {
        persistHint(HINT_KEYS.seenIncomplete);
      }
      return;
    }
    if (gettingStartedStorage.isCelebrated(dismissedHints, userId)) return;
    const wasSeenIncomplete = gettingStartedStorage.isSeenIncomplete(dismissedHints, userId);
    persistHint(HINT_KEYS.celebrated);
    setCelebrated(true);
    if (wasSeenIncomplete) {
      setShowCelebration(true);
    }
  }, [isReady, allDone, dismissedHints, userId, persistHint]);

  // Fire the first_data_point activation event exactly once, the first time
  // first-data is observed done. Runs only once the checklist is ready so a
  // loading placeholder never counts as "no data". The durable 'ttfdp-tracked'
  // hint is the once-ever guard; the ref latch prevents a same-session
  // double-fire before that hint round-trips. `source` is derived cheaply from
  // the same signals first-data reads (device mapping vs a manual measurement).
  useEffect(() => {
    if (!isReady || !hasData) return;
    if (firstDataTrackedRef.current) return;
    if (gettingStartedStorage.isFirstDataPointTracked(dismissedHints)) return;
    firstDataTrackedRef.current = true;
    persistHint(HINT_KEYS.firstDataPointTracked);
    const source = hasDeviceParams ? 'device' : hasLatestMetric ? 'manual' : 'unknown';
    trackOnboarding('first_data_point', { source });
  }, [isReady, hasData, hasDeviceParams, hasLatestMetric, dismissedHints, persistHint]);

  return {
    items,
    completedCount,
    allDone,
    isReady,
    showCelebration,
    dismissed,
    dismiss,
    restore,
    celebrated,
    markCelebrated,
  };
}
